import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import { showAlert } from '../common/utils'
import { WhiteSpace } from 'antd-mobile'
import { Button, Toast } from 'antd-mobile-v5'
import { useHistory } from 'react-router-dom'
import { isTaiwanUser } from '../../utils/user'
import { getWechatOpenId } from '../../utils/wechat'
import AgreementPopup from './AgreementPopup'
import FuiouQrPopup from './FuiouQrPopup'
import {
  isFuiouQrChannel,
  isValidWposQrContent,
  pickFuiouChannel,
  safeExternalRedirect,
  safeFuiouRedirect,
  createWposPayTypePicker,
  FUIOU_H5_PAY_TYPE,
  FUIOU_PAY_TYPES,
} from './utils'

const PaymentBtn = ({ code, paymentFn, payApplicationType }) => {
  const { push } = useHistory()
  const [isSubmitting, setSubmitting] = useState(false)
  const { formatMessage } = useIntl()
  const [visible, setVisible] = useState(false)
  const [qrVisible, setQrVisible] = useState(false)
  const [qrUrl, setQrUrl] = useState('')
  // 应付金额（CHNA-2618：申请类端点本就返回 price/currency，透传给弹窗展示）
  const [qrPrice, setQrPrice] = useState(null)
  const [qrCurrency, setQrCurrency] = useState(null)
  const [qrPayType, setQrPayType] = useState(null)
  const [qrCode, setQrCode] = useState('')
  const wposPickerRef = useRef(null)
  // await picker 后组件可能已卸载：setState 前判 mounted 防卸载告警
  const mountedRef = useRef(true)
  const { profile, userInfo } = useSelector((state) => ({
    profile: state.data.profile,
    userInfo: state.data.userInfo,
  }))

  // 组件卸载时清理残留的命令式渠道选择面板
  useEffect(() => {
    // StrictMode 下 effect 会 setup→cleanup→setup 重跑：cleanup 置 false 后必须复位，
    // 否则重挂载后 mountedRef 恒 false，支付回调全部失效
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (wposPickerRef.current) {
        wposPickerRef.current.close()
        wposPickerRef.current = null
      }
    }
  }, [])

  const supportsFuiou = FUIOU_PAY_TYPES.includes(payApplicationType)

  const callPaymentFn = async () => {
    // TW 用户/海外环境不参与富友迁移：直接走旧支付通道（后端按地区路由 TradeVan 整页跳转），
    // 不弹微信/支付宝渠道选择
    if (profile.teachingLocation === 'TW' || isTaiwanUser()) return paymentFn(code)
    if (!supportsFuiou) return paymentFn(code)
    const channel = pickFuiouChannel()
    // 微信内 H5PAY 用 WeJsapiPay；PC WPOS 先选渠道（微信/支付宝），FAPPLET 聚合码已下线
    if (channel === 'H5PAY')
      return paymentFn({ code, channel, payType: FUIOU_H5_PAY_TYPE.WECHAT_JSAPI, openid: getWechatOpenId() })
    const picker = createWposPayTypePicker(formatMessage)
    wposPickerRef.current = picker
    const payType = await picker.promise
    wposPickerRef.current = null
    // 用户取消渠道选择，静默返回
    if (!payType) return null
    if (mountedRef.current) setQrPayType(payType)
    return paymentFn({ code, channel, payType })
  }

  const handleFuiouResult = (result) => {
    if (isFuiouQrChannel()) {
      // QR 渲染不经 safeFuiouRedirect，此处单独过二维码内容白名单
      // （单码通道返回渠道二维码内容，兼容历史富友聚合链接形态）
      if (!isValidWposQrContent(result.url)) {
        Toast.show({ icon: 'fail', content: formatMessage({ id: 'fuiou-pay-url-invalid' }) })
        return
      }
      setQrUrl(result.url)
      setQrPrice(result.price)
      setQrCurrency(result.currency)
      setQrCode(result.code || code)
      setQrVisible(true)
      return
    }
    safeFuiouRedirect(result.url, formatMessage({ id: 'fuiou-pay-url-invalid' }))
  }

  const toPay = async () => {
    try {
      setSubmitting(true)
      const result = await callPaymentFn()
      // 用户取消 WPOS 渠道选择
      if (!result) return
      if (result.error) {
        showAlert(result.error.clientMsg, formatMessage({ id: 'common-close' }))
        return
      }
      if (!result.url) {
        // 下单成功但无 url（异常形态）：提示失败，避免静默无反馈
        Toast.show({ icon: 'fail', content: formatMessage({ id: 'fuiou-pay-fail' }) })
        return
      }
      if (supportsFuiou && result.paymentMethodType === 'FUIOU') {
        handleFuiouResult(result)
        return
      }
      if (profile.teachingLocation === 'TW')
        return push('/invoice_form', {
          code,
          nextUrl: result.url,
          type: payApplicationType,
        })
      safeExternalRedirect(result.url, formatMessage({ id: 'fuiou-pay-url-invalid' }), {
        // 白名单外域名弹确认（message 由 safeExternalRedirect 以 hostname 调用）
        message: (host) => formatMessage({ id: 'fuiou-external-redirect-confirm' }, { host }),
        confirm: formatMessage({ id: 'common-comfirmBtn' }),
        cancel: formatMessage({ id: 'common-cancel' }),
      })
    } catch (error) {
      // paymentFn 异常（runAsync 失败恒 reject，无 {error} 信封）时给用户可见提示
      showAlert(
        (error && error.clientMsg) || formatMessage({ id: 'fuiou-pay-fail' }),
        formatMessage({ id: 'common-close' })
      )
    } finally {
      setSubmitting(false)
    }
  }

  const verifyIpBeforePay = () => {
    if (profile.teachingLocation === 'TW' || isTaiwanUser()) {
      setVisible(true)
    } else {
      toPay()
    }
  }

  const onPaymentSuccess = useCallback(() => {
    setQrVisible(false)
    Toast.show({
      icon: 'success',
      content: formatMessage({ id: 'fuiou-pay-success' }),
      afterClose: () => window.location.reload(),
    })
  }, [formatMessage])

  const handleCloseQr = useCallback(() => {
    setQrVisible(false)
  }, [])

  if (!userInfo || !profile) return null

  return (
    <>
      <AgreementPopup visible={visible} setVisible={setVisible} toPay={toPay} />
      <FuiouQrPopup
        visible={qrVisible}
        url={qrUrl}
        price={qrPrice}
        currency={qrCurrency}
        onClose={handleCloseQr}
        onPaymentSuccess={onPaymentSuccess}
        payApplicationType={payApplicationType}
        code={qrCode || code}
        payType={qrPayType}
      />
      <WhiteSpace />
      <Button block color="primary" onClick={verifyIpBeforePay} loading={isSubmitting} disabled={isSubmitting}>
        <FormattedMessage id="app-to-pay" />
      </Button>
    </>
  )
}

export default PaymentBtn
